/**
 * The self-service gate decision, as pure data.
 *
 * Kept apart from the `SelfServiceGate` component so the branches are unit-
 * testable as a table and the component file exports only a component
 * (react-refresh). See `components/SelfServiceGate.tsx` for the why of each
 * redirect.
 */
import type { Team } from 'api-client';
import { BillingModeEnum } from 'api-client';

import {
  FIRST_STEP_PATH,
  GET_STARTED_PATH,
  selfServiceStepPath,
} from '@/lib/self-service/constants';

const LOGIN_PATH = '/account/login';
const APP_HOME_PATH = '/';
const TRIAL_STEP_PATH = selfServiceStepPath('trial');

export type GateState =
  | { kind: 'loading' }
  | { kind: 'redirect'; to: string }
  | { kind: 'ready' };

export interface GateInputs {
  /** `undefined` while the status request is in flight. */
  enabled: boolean | undefined;
  statusFailed: boolean;
  isAuthenticated: boolean;
  orgsLoaded: boolean;
  orgsFailed: boolean;
  hasOrg: boolean;
  /**
   * The user's ONLY org is a self-service signup whose Stripe Checkout was
   * never completed (see `isUnfinishedCheckout`). They are still mid-signup:
   * the wizard's trial step re-POSTs `confirm`, which resumes that org and
   * mints a fresh Checkout session.
   */
  hasOnlyUnfinishedCheckout: boolean;
  /** Wizard steps require a session; the create-account screen does not. */
  requireAuth: boolean;
}

/**
 * `confirm` creates the org BEFORE Stripe (org-first, so a partial confirm can
 * be repaired) with `pending_invite_checkout` set until the webhook lands. An
 * org still in that state with no subscription is a checkout the founder
 * abandoned, e.g. via Stripe's Back button.
 */
export function isUnfinishedCheckout(org: Team): boolean {
  return (
    org.billingMode === BillingModeEnum.Stripe &&
    !org.hasActiveSubscription &&
    org.pendingInviteCheckout === true
  );
}

export function resolveGateState(inputs: GateInputs): GateState {
  if (inputs.statusFailed || inputs.enabled === false) {
    return { kind: 'redirect', to: LOGIN_PATH };
  }
  if (inputs.enabled === undefined) {
    return { kind: 'loading' };
  }
  if (!inputs.isAuthenticated) {
    return inputs.requireAuth
      ? { kind: 'redirect', to: GET_STARTED_PATH }
      : { kind: 'ready' };
  }
  // Signed in: wait for the org list before deciding where they belong. A
  // failed org load can't prove either way, so it falls through to the flow.
  if (!inputs.orgsLoaded && !inputs.orgsFailed) {
    return { kind: 'loading' };
  }
  if (inputs.hasOnlyUnfinishedCheckout) {
    // Back in the wizard to finish paying — any step is fine once inside it.
    return inputs.requireAuth
      ? { kind: 'ready' }
      : { kind: 'redirect', to: TRIAL_STEP_PATH };
  }
  if (inputs.hasOrg) {
    return { kind: 'redirect', to: APP_HOME_PATH };
  }
  if (!inputs.requireAuth) {
    return { kind: 'redirect', to: FIRST_STEP_PATH };
  }
  return { kind: 'ready' };
}
